import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CaretLeft,
  PaperPlaneTilt,
  Briefcase,
  PushPin,
  PushPinSlash,
  DotsThreeVertical,
  SignOut,
  UserMinus,
  Check,
  Checks,
  Clock,
  DotsThree,
  Spinner,
  ArrowDown,
  Paperclip,
  X,
  File,
  FilePdf,
  FileDoc,
  FileText,
  DownloadSimple,
  ShieldCheck,
  Scales,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchMessages,
  sendMessage as apiSendMessage,
  markThreadRead,
  leaveThread as apiLeaveThread,
  removeThreadParticipant,
  pinMessage,
  unpinMessage,
  fetchPinnedMessages,
  type MessageThread,
  type ChatMessage,
  type MessageType,
  MESSAGE_TYPE_LABELS,
  MESSAGE_TYPE_COLOURS,
} from '@/lib/messagingApi';
import {
  onMessage,
  onTypingStart,
  onTypingStop,
  emitTypingStart,
  emitTypingStop,
  useWebSocketStore,
} from '@/lib/websocket';
import { fetchDocuments, type DocFile } from '@/lib/documentsApi';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { cn } from '@/lib/utils';

interface Props {
  thread: MessageThread;
  onLeftThread: () => void;
  /**
   * Below `md` the messaging view is master/detail rather than two columns: opening a thread
   * replaces the conversation list, so the pane needs its own way back to it. Absent on desktop,
   * where the list is still on screen beside this pane.
   */
  onBack?: () => void;
}

interface TypingUser {
  odUserId: string;
  userName: string;
  startedAt: number;
}

export function ThreadPane({ thread, onLeftThread, onBack }: Props) {
  const currentUser = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const wsConnected = useWebSocketStore((s) => s.connected);
  const [composer, setComposer] = useState('');
  const [messageType, setMessageType] = useState<MessageType>('general');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPinned, setShowPinned] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showAttachPicker, setShowAttachPicker] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<DocFile[]>([]);
  const attachPickerRef = useRef<HTMLDivElement | null>(null);
  // A shared document opens in the product's standard viewer rather than only downloading.
  const [previewDoc, setPreviewDoc] = useState<{ id: string; name: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch messages from real API
  const {
    data: messages = [],
    isLoading: messagesLoading,
  } = useQuery({
    queryKey: ['messaging', 'messages', thread.id],
    queryFn: () => fetchMessages(thread.id, { limit: 100 }),
    enabled: !!thread.id && !!currentUser,
    staleTime: 10_000,
  });

  // Fetch pinned messages
  const { data: pinnedMessages = [] } = useQuery({
    queryKey: ['messaging', 'pinned', thread.id],
    queryFn: () => fetchPinnedMessages(thread.id),
    enabled: !!thread.id && showPinned,
  });

  // Fetch documents for attachment picker
  const { data: availableDocs = [] } = useQuery({
    queryKey: ['documents', 'for-attach', thread.matterId],
    queryFn: () => fetchDocuments(thread.matterId ?? undefined),
    enabled: showAttachPicker,
    staleTime: 30_000,
  });

  // Send message mutation with optimistic update
  const sendMutation = useMutation({
    mutationFn: ({ text, documentIds, msgType }: { text: string; documentIds?: string[]; msgType: MessageType }) =>
      apiSendMessage(thread.id, text, { messageType: msgType, documentIds }),
    onMutate: async ({ text, msgType }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['messaging', 'messages', thread.id] });

      // Snapshot previous messages
      const previous = queryClient.getQueryData<ChatMessage[]>(['messaging', 'messages', thread.id]);

      // Optimistically add the message
      if (currentUser) {
        const optimisticMessage: ChatMessage = {
          id: `temp-${Date.now()}`,
          threadId: thread.id,
          senderUserId: currentUser.id,
          body: text,
          messageType: msgType,
          sequence: (previous?.length ?? 0) + 1,
          createdAt: new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
          pinnedAt: null,
          pinnedById: null,
          senderUser: {
            id: currentUser.id,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            memberType: currentUser.memberType,
          },
          pinnedBy: null,
          attachments: [],
          seenByOthers: false,
        };

        queryClient.setQueryData<ChatMessage[]>(
          ['messaging', 'messages', thread.id],
          (old) => [...(old ?? []), optimisticMessage],
        );
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(['messaging', 'messages', thread.id], context.previous);
      }
      toast.error('Failed to send message');
    },
    onSuccess: () => {
      // Invalidate to get the real message with correct ID
      queryClient.invalidateQueries({ queryKey: ['messaging', 'messages', thread.id] });
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
    },
  });

  // Mark thread read mutation
  const markReadMutation = useMutation({
    mutationFn: (upToSequence: number) => markThreadRead(thread.id, upToSequence),
    onSuccess: () => {
      // Invalidate threads to update unread counts in sidebar
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
    },
  });

  // Leave thread mutation
  const leaveMutation = useMutation({
    mutationFn: () => apiLeaveThread(thread.id),
    onSuccess: () => {
      toast.success('You left the conversation');
      onLeftThread();
    },
    onError: () => {
      toast.error('Failed to leave conversation');
    },
  });

  // Remove participant mutation
  const removeParticipantMutation = useMutation({
    mutationFn: (userId: string) => removeThreadParticipant(thread.id, userId),
    onSuccess: () => {
      toast.success('Participant removed');
      queryClient.invalidateQueries({ queryKey: ['messaging', 'threads'] });
      setMenuOpen(false);
    },
    onError: () => {
      toast.error('Failed to remove participant');
    },
  });

  // Pin/unpin message mutations
  const pinMutation = useMutation({
    mutationFn: (messageId: string) => pinMessage(thread.id, messageId),
    onSuccess: () => {
      toast.success('Message pinned');
      queryClient.invalidateQueries({ queryKey: ['messaging', 'messages', thread.id] });
      queryClient.invalidateQueries({ queryKey: ['messaging', 'pinned', thread.id] });
    },
  });

  const unpinMutation = useMutation({
    mutationFn: (messageId: string) => unpinMessage(thread.id, messageId),
    onSuccess: () => {
      toast.success('Message unpinned');
      queryClient.invalidateQueries({ queryKey: ['messaging', 'messages', thread.id] });
      queryClient.invalidateQueries({ queryKey: ['messaging', 'pinned', thread.id] });
    },
  });

  // Subscribe to real-time message events.
  // Body is not included in the WS signal — fetch full message list via HTTP.
  // This keeps message content off the WebSocket layer (Phase 2.5-B security fix).
  useEffect(() => {
    const unsubscribe = onMessage((msg) => {
      if (msg.threadId === thread.id) {
        queryClient.invalidateQueries({ queryKey: ['messaging', 'messages', thread.id] });
      }
    });
    return unsubscribe;
  }, [thread.id, queryClient, wsConnected]);

  // Subscribe to typing indicators
  useEffect(() => {
    const unsubStart = onTypingStart((evt) => {
      if (evt.threadId === thread.id && evt.userId !== currentUser?.id) {
        setTypingUsers((prev) => {
          // Remove existing entry for this user and add fresh one
          const filtered = prev.filter((t) => t.odUserId !== evt.userId);
          const participant = thread.participants.find((p) => p.userId === evt.userId);
          const userName = participant
            ? `${participant.user.firstName}`
            : 'Someone';
          return [...filtered, { odUserId: evt.userId, userName, startedAt: Date.now() }];
        });
      }
    });

    const unsubStop = onTypingStop((evt) => {
      if (evt.threadId === thread.id) {
        setTypingUsers((prev) => prev.filter((t) => t.odUserId !== evt.userId));
      }
    });

    return () => {
      unsubStart();
      unsubStop();
    };
  }, [thread.id, thread.participants, currentUser?.id, wsConnected]);

  // Clear stale typing indicators periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => prev.filter((t) => now - t.startedAt < 5000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!showScrollToBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, showScrollToBottom]);

  // Track scroll position to show "scroll to bottom" button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollToBottom(!isNearBottom);
  }, []);

  // Mark-read only when user is near bottom of scroll (not on initial load)
  useEffect(() => {
    const last = messages.at(-1);
    if (!last || !currentUser) return;
    // Only mark read if user is viewing bottom of conversation
    if (!showScrollToBottom) {
      markReadMutation.mutate(last.sequence);
    }
  }, [thread.id, messages, currentUser, showScrollToBottom]);

  // Click-outside the menu and attachment picker
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (attachPickerRef.current && !attachPickerRef.current.contains(e.target as Node)) {
        setShowAttachPicker(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Handle user typing - emit WebSocket event with debounce
  const handleTyping = useCallback(() => {
    if (!currentUser || isTyping) return;

    setIsTyping(true);
    emitTypingStart(thread.id);

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      emitTypingStop(thread.id);
    }, 3000);
  }, [thread.id, currentUser, isTyping]);

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = composer.trim();
    if ((!text && selectedDocs.length === 0) || sendMutation.isPending || !currentUser) return;

    // Clear typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setIsTyping(false);
    emitTypingStop(thread.id);

    const documentIds = selectedDocs.length > 0 ? selectedDocs.map((d) => d.id) : undefined;
    sendMutation.mutate({ text: text || `Attached ${selectedDocs.length} document(s)`, documentIds, msgType: messageType });
    setMessageType('general');
    setComposer('');
    setSelectedDocs([]);
    setShowAttachPicker(false);
  };

  const toggleDoc = (doc: DocFile) => {
    setSelectedDocs((prev) =>
      prev.some((d) => d.id === doc.id)
        ? prev.filter((d) => d.id !== doc.id)
        : [...prev, doc]
    );
  };

  const removeSelectedDoc = (docId: string) => {
    setSelectedDocs((prev) => prev.filter((d) => d.id !== docId));
  };

  const handleLeave = () => {
    if (window.confirm('Leave this conversation? You will not receive new messages from it.')) {
      leaveMutation.mutate();
    }
  };

  const handlePrintTranscript = () => {
    const threadName = threadDisplayName(thread, currentUser?.id);
    const privilegeLabel = thread.privilege === 'legal_advice'
      ? 'LEGALLY PRIVILEGED — Legal Advice Privilege'
      : thread.privilege === 'without_prejudice'
      ? 'WITHOUT PREJUDICE — Not admissible as evidence'
      : null;

    const lines = messages.map((m) => {
      const sender = m.senderUser
        ? `${m.senderUser.firstName} ${m.senderUser.lastName}`
        : 'System';
      const ts = new Date(m.createdAt).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      const typeLabel = m.messageType && m.messageType !== 'general'
        ? ` [${m.messageType.replace(/_/g, ' ').toUpperCase()}]`
        : '';
      const attachments = m.attachments?.map(a => `\n    📎 ${a.document.name}`).join('') ?? '';
      return `[${ts}]${typeLabel} ${sender}:\n${m.body}${attachments}`;
    }).join('\n\n');

    const matterRef = thread.case
      ? `Matter: ${thread.case.caseNumber} — ${thread.case.caseName}\n`
      : '';
    const participants = thread.participants
      .map(p => `${p.user.firstName} ${p.user.lastName} (${p.role})`)
      .join(', ');

    const content = [
      `COMMUNICATION TRANSCRIPT`,
      `${'─'.repeat(60)}`,
      `Subject: ${thread.subject ?? '(no subject)'}`,
      matterRef,
      `Participants: ${participants}`,
      `Exported: ${new Date().toLocaleString('en-GB')}`,
      privilegeLabel ? `\n⚠ ${privilegeLabel}` : '',
      `${'─'.repeat(60)}\n`,
      lines,
      `\n${'─'.repeat(60)}`,
      `End of transcript — ${messages.length} messages`,
    ].filter(Boolean).join('\n');

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>Transcript — ${threadName}</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; line-height: 1.6; margin: 40px; color: #111; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
            @media print { body { margin: 20mm; } }
          </style>
        </head>
        <body><pre>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></body>
      </html>
    `);
    win.document.close();
    win.print();
    setMenuOpen(false);
  };

  const handleRemoveParticipant = (userId: string, userName: string) => {
    if (window.confirm(`Remove ${userName} from this conversation?`)) {
      removeParticipantMutation.mutate(userId);
    }
  };

  const handlePinToggle = (messageId: string, isPinned: boolean) => {
    if (isPinned) {
      unpinMutation.mutate(messageId);
    } else {
      pinMutation.mutate(messageId);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollToBottom(false);
  };

  const me = useMemo(
    () => thread.participants.find((p) => p.userId === currentUser?.id),
    [thread, currentUser?.id],
  );
  const isOwner = me?.role === 'owner';
  const otherParticipants = thread.participants.filter(
    (p) => p.userId !== currentUser?.id,
  );

  // Get count of messages with pinnedAt
  const pinnedCount = useMemo(
    () => messages.filter((m) => m.pinnedAt).length,
    [messages],
  );

  return (
    <Card className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-5 py-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="-ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Back to conversations"
          >
            <CaretLeft className="h-4 w-4" weight="bold" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {threadDisplayName(thread, currentUser?.id)}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {otherParticipants.length === 0
              ? 'Just you'
              : otherParticipants
                  .map((p) => `${p.user.firstName} ${p.user.lastName}`)
                  .join(' · ')}
          </div>
        </div>
        {thread.case && (
          <Badge variant="outline" className="shrink-0 gap-1">
            <Briefcase size={11} weight="duotone" />
            {thread.case.caseNumber}
          </Badge>
        )}
        {pinnedCount > 0 && (
          <button
            onClick={() => setShowPinned((s) => !s)}
            className={cn(
              'flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium transition',
              showPinned
                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30'
                : 'text-muted-foreground hover:bg-muted',
            )}
            title="Pinned messages"
          >
            <PushPin size={12} weight="fill" />
            {pinnedCount}
          </button>
        )}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen((s) => !s)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Conversation menu"
          >
            <DotsThreeVertical size={16} weight="bold" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-20 w-56 overflow-hidden rounded-md border border-border bg-card shadow-pop">
              <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-[0.06em] text-muted-foreground">
                Participants
              </div>
              <div className="max-h-48 overflow-y-auto py-1">
                {thread.participants.map((p) => (
                  <div
                    key={p.id}
                    className="group flex items-center justify-between gap-2 px-3 py-1.5 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {p.user.firstName} {p.user.lastName}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                        {p.role}
                      </div>
                    </div>
                    {isOwner && p.userId !== currentUser?.id && (
                      <button
                        onClick={() => handleRemoveParticipant(p.userId, `${p.user.firstName} ${p.user.lastName}`)}
                        className="opacity-0 transition group-hover:opacity-100 hover:text-red-600 dark:hover:text-red-400"
                        title="Remove"
                        disabled={removeParticipantMutation.isPending}
                      >
                        <UserMinus size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-border">
                <button
                  onClick={handlePrintTranscript}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <ArrowDown size={13} weight="duotone" />
                  Print transcript
                </button>
                <button
                  onClick={handleLeave}
                  disabled={leaveMutation.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 dark:text-red-400 transition hover:bg-red-50 dark:bg-red-500/10 disabled:opacity-50"
                >
                  <SignOut size={13} weight="duotone" />
                  Leave conversation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Privilege warning banner */}
      {thread.privilege !== 'none' && (
        <div className={cn(
          'flex items-center gap-2 border-b px-4 py-2',
          thread.privilege === 'legal_advice'
            ? 'border-blue-200 dark:border-blue-500/30 bg-blue-50/80 dark:bg-blue-500/10 text-blue-900 dark:text-blue-200'
            : 'border-amber-200 dark:border-amber-500/30 bg-amber-50/80 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'
        )}>
          {thread.privilege === 'legal_advice' ? (
            <>
              <ShieldCheck size={16} weight="duotone" className="shrink-0 text-blue-600 dark:text-blue-400" />
              <div className="min-w-0 flex-1 text-xs">
                <span className="font-semibold">Legal Advice Privilege</span>
                <span className="mx-1.5">·</span>
                <span className="text-blue-700 dark:text-blue-300">
                  This conversation is protected. Do not forward or disclose to third parties.
                </span>
              </div>
            </>
          ) : (
            <>
              <Scales size={16} weight="duotone" className="shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 flex-1 text-xs">
                <span className="font-semibold">Without Prejudice</span>
                <span className="mx-1.5">·</span>
                <span className="text-amber-700 dark:text-amber-300">
                  Settlement discussion. Cannot be used as evidence in court proceedings.
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Pinned drawer */}
      {showPinned && pinnedMessages.length > 0 && (
        <div className="border-b border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-5 py-2">
          <div className="mb-1 flex items-center justify-between text-xs uppercase tracking-[0.06em] text-amber-800 dark:text-amber-300">
            <span className="flex items-center gap-1">
              <PushPin size={11} weight="fill" />
              Pinned messages
            </span>
            <button
              onClick={() => setShowPinned(false)}
              className="text-amber-700 hover:text-amber-900 dark:hover:text-amber-200 dark:text-amber-200"
            >
              Hide
            </button>
          </div>
          <div className="space-y-1.5">
            {pinnedMessages.map((m) => (
              <div key={m.id} className="text-xs leading-snug text-amber-950">
                <span className="font-semibold">
                  {m.senderUser
                    ? `${m.senderUser.firstName} ${m.senderUser.lastName}`
                    : 'Unknown'}
                  :
                </span>{' '}
                <span className="line-clamp-2">{m.body}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="relative flex-1 space-y-1 overflow-y-auto px-5 py-4"
      >
        {messagesLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}
        {!messagesLoading && messages.map((m, idx) => {
          const prev = idx > 0 ? messages[idx - 1] : null;
          const showDay = !prev || !sameDay(prev.createdAt, m.createdAt);
          const sameSenderAsPrev = prev && prev.senderUserId === m.senderUserId;
          const closeInTime =
            prev &&
            new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60_000;
          const showHeader = !(sameSenderAsPrev && closeInTime && !showDay);
          return (
            <div key={m.id}>
              {showDay && (
                <div className="my-3 flex items-center gap-2 text-center">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    {formatDay(m.createdAt)}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <MessageBubble
                m={m}
                isMe={m.senderUserId === currentUser?.id}
                showHeader={showHeader}
                onPinToggle={() => handlePinToggle(m.id, Boolean(m.pinnedAt))}
                onOpenAttachment={(documentId, name) => setPreviewDoc({ id: documentId, name })}
              />
            </div>
          );
        })}
        {!messagesLoading && messages.length === 0 && (
          <div className="py-12 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-50">
              <PaperPlaneTilt size={18} weight="duotone" className="text-brand-600 dark:text-brand-400" />
            </div>
            <p className="text-sm font-medium text-foreground">Be the first to send a message</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Start the conversation with your colleagues.
            </p>
          </div>
        )}
        <div ref={messagesEndRef} />

        {/* Scroll to bottom button */}
        {showScrollToBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition hover:bg-brand-700"
          >
            <ArrowDown size={18} weight="bold" />
          </button>
        )}
      </div>

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <div className="flex items-center gap-2 border-t border-border/50 bg-muted/30 px-4 py-2">
          <div className="flex items-center gap-1">
            <DotsThree size={16} weight="bold" className="animate-pulse text-brand-600 dark:text-brand-400" />
            <span className="text-xs text-muted-foreground">
              {typingUsers.length === 1
                ? `${typingUsers[0]?.userName ?? 'Someone'} is typing...`
                : `${typingUsers.map(t => t.userName).join(', ')} are typing...`
              }
            </span>
          </div>
        </div>
      )}

      {/* Selected documents preview */}
      {selectedDocs.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-border/50 bg-muted/20 px-4 py-2">
          {selectedDocs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs"
            >
              <DocIcon icon={doc.icon} size={12} />
              <span className="max-w-[120px] truncate">{doc.name}</span>
              <button
                type="button"
                onClick={() => removeSelectedDoc(doc.id)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Message type selector — only shown for matter threads where type matters operationally */}
      {/* Five labels plus the "Type:" caption do not fit a phone — "Fee discussion" sat past the
          edge, so that type could not be chosen. The row scrolls sideways; the vertical axis is
          pinned so it cannot slide and clip the labels the way the matter tabs did. */}
      {(thread.kind === 'matter_internal' || thread.kind === 'matter_external') && (
        <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto overflow-y-hidden border-t border-border/50 bg-muted/20 px-4 py-1.5">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Type:</span>
          {(['general', 'urgent', 'instruction', 'hearing_update', 'fee_discussion'] as MessageType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setMessageType(t)}
              className={cn(
                'shrink-0 rounded border px-2 py-0.5 text-[10px] font-medium transition',
                messageType === t
                  ? t === 'general'
                    ? 'border-border bg-muted text-foreground'
                    : cn('border font-semibold', MESSAGE_TYPE_COLOURS[t])
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
              )}
            >
              {MESSAGE_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="flex items-end gap-2 border-t border-border bg-background px-4 py-3"
      >
        {/* Attachment button with picker */}
        <div ref={attachPickerRef} className="relative">
          <button
            type="button"
            onClick={() => setShowAttachPicker((s) => !s)}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-md border border-input transition',
              showAttachPicker || selectedDocs.length > 0
                ? 'bg-brand-50 text-brand-600 dark:text-brand-400 border-brand-200'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Attach document"
          >
            <Paperclip size={16} />
            {selectedDocs.length > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                {selectedDocs.length}
              </span>
            )}
          </button>

          {/* Attachment picker popover */}
          {showAttachPicker && (
            <div className="absolute bottom-full left-0 z-30 mb-2 w-72 overflow-hidden rounded-lg border border-border bg-card shadow-pop">
              <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Attach Documents
              </div>
              <div className="max-h-48 overflow-y-auto">
                {availableDocs.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                    No documents available
                  </div>
                ) : (
                  availableDocs.map((doc) => {
                    const isSelected = selectedDocs.some((d) => d.id === doc.id);
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => toggleDoc(doc)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-muted/50',
                          isSelected && 'bg-brand-50'
                        )}
                      >
                        <DocIcon icon={doc.icon} size={16} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-medium">{doc.name}</div>
                          <div className="text-[10px] text-muted-foreground">{doc.size} · {doc.kind}</div>
                        </div>
                        {isSelected && (
                          <Check size={14} className="shrink-0 text-brand-600 dark:text-brand-400" weight="bold" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <textarea
          value={composer}
          onChange={(e) => {
            setComposer(e.target.value);
            handleTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Write a message — Enter to send, Shift+Enter for newline"
          rows={2}
          maxLength={4000}
          className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
        />
        <Button
          type="submit"
          disabled={(!composer.trim() && selectedDocs.length === 0) || sendMutation.isPending}
          className="h-9 gap-1.5 px-3"
        >
          {sendMutation.isPending ? (
            <Spinner size={14} className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={14} weight="fill" />
          )}
          Send
        </Button>
      </form>

      <DocumentPreviewModal
        documentId={previewDoc?.id ?? null}
        documentName={previewDoc?.name}
        onClose={() => setPreviewDoc(null)}
      />
    </Card>
  );
}

// ─── Message bubble ─────────────────────────────────────────────

interface BubbleProps {
  m: ChatMessage;
  isMe: boolean;
  showHeader: boolean;
  onPinToggle: () => void;
  onOpenAttachment: (documentId: string, name: string) => void;
}

function MessageBubble({ m, isMe, showHeader, onPinToggle, onOpenAttachment }: BubbleProps) {
  const sender = m.senderUser
    ? `${m.senderUser.firstName} ${m.senderUser.lastName}`
    : 'Unknown';
  const initials = m.senderUser
    ? `${m.senderUser.firstName[0] ?? ''}${m.senderUser.lastName[0] ?? ''}`.toUpperCase()
    : '?';

  const isOptimistic = m.id.startsWith('temp-');
  const status = isOptimistic ? 'sending' : m.seenByOthers ? 'seen' : 'delivered';

  return (
    <div
      className={cn(
        'group flex',
        isMe ? 'flex-row-reverse' : 'flex-row',
        showHeader ? 'mt-5 first:mt-0' : 'mt-1',
        'items-end gap-2.5',
      )}
    >
      {/* Avatar — received messages only. Empty spacer for consecutive messages. */}
      {!isMe && (
        <div className="mb-0.5 shrink-0">
          {showHeader ? (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:text-brand-300">
              {initials}
            </div>
          ) : (
            <div className="h-8 w-8" /> // spacer keeps text alignment
          )}
        </div>
      )}

      {/* Bubble + meta */}
      <div className={cn('flex min-w-0 max-w-[85%] flex-col', isMe ? 'items-end' : 'items-start')}>
        {/* Header row: sender name · time · type badge */}
        {showHeader && (
          <div className={cn(
            'mb-1.5 flex items-center gap-2 text-xs text-muted-foreground',
            isMe ? 'flex-row-reverse' : 'flex-row',
          )}>
            <span className="font-semibold text-foreground/80">{isMe ? 'You' : sender}</span>
            <span className="text-muted-foreground/60">·</span>
            <span>{formatTime(m.createdAt)}</span>
            {m.messageType && m.messageType !== 'general' && (
              <span className={cn(
                'rounded border px-1.5 py-0.5 text-[9px] font-semibold leading-none',
                MESSAGE_TYPE_COLOURS[m.messageType as keyof typeof MESSAGE_TYPE_COLOURS] || '',
              )}>
                {MESSAGE_TYPE_LABELS[m.messageType as keyof typeof MESSAGE_TYPE_LABELS] ?? m.messageType}
              </span>
            )}
          </div>
        )}

        {/* Bubble row with pin button */}
        <div className="flex items-end gap-1.5">
          {isMe && (
            <button
              onClick={onPinToggle}
              className="opacity-0 transition group-hover:opacity-100 hover:text-amber-600 dark:hover:text-amber-400"
              title={m.pinnedAt ? 'Unpin' : 'Pin'}
            >
              {m.pinnedAt ? <PushPinSlash size={13} className="text-amber-600 dark:text-amber-400" /> : <PushPin size={13} className="text-muted-foreground" />}
            </button>
          )}

          <div
            className={cn(
              'relative whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
              isMe
                ? 'rounded-tr-sm bg-brand-600 text-white'
                : 'rounded-tl-sm bg-muted text-foreground',
              m.pinnedAt && 'ring-1 ring-amber-400/60',
            )}
            title={new Date(m.createdAt).toLocaleString()}
          >
            {m.pinnedAt && (
              <PushPin size={10} weight="fill" className="absolute -right-1 -top-1 rounded-full bg-amber-500 p-0.5 text-white shadow-sm" />
            )}
            {m.body}
            {m.attachments && m.attachments.length > 0 && (
              <div className={cn('mt-2.5 space-y-1.5', !m.body && '-mt-0.5')}>
                {m.attachments.map((att) => (
                  // Opens the SAME viewer as every other surface in the product (documents,
                  // matter, hearing, court mode). It streams through the authenticated endpoint
                  // and offers download inside. The chip was previously an <a href={fileUrl}>,
                  // and `Document.fileUrl` is null for every document — storage keys stay
                  // server-side — so a shared document could never actually be opened.
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => onOpenAttachment(att.documentId, att.document.name)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition',
                      isMe ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-background hover:bg-accent text-foreground',
                    )}
                  >
                    <AttachmentIcon mimeType={att.document.mimeType} size={14} isMe={isMe} />
                    <span className="min-w-0 flex-1 truncate">{att.document.name}</span>
                    <DownloadSimple size={12} className="shrink-0 opacity-60" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {!isMe && (
            <button
              onClick={onPinToggle}
              className="opacity-0 transition group-hover:opacity-100 hover:text-amber-600 dark:hover:text-amber-400"
              title={m.pinnedAt ? 'Unpin' : 'Pin'}
            >
              {m.pinnedAt ? <PushPinSlash size={13} className="text-amber-600 dark:text-amber-400" /> : <PushPin size={13} className="text-muted-foreground" />}
            </button>
          )}
        </div>

        {/* Tick state — outgoing messages only */}
        {isMe && (
          <div className="mt-0.5 flex items-center gap-0.5 text-[10px] text-muted-foreground">
            <span>{formatTime(m.createdAt)}</span>
            <TickIndicator status={status} />
          </div>
        )}
      </div>
    </div>
  );
}

function TickIndicator({ status }: { status: 'sending' | 'sent' | 'delivered' | 'seen' }) {
  switch (status) {
    case 'sending':
      return <Clock size={11} className="ml-0.5 animate-pulse text-muted-foreground" />;
    case 'sent':
      return <Check size={12} className="ml-0.5 text-muted-foreground" />;
    case 'delivered':
      return <Checks size={12} className="ml-0.5 text-muted-foreground" />;
    case 'seen':
      return <Checks size={12} className="ml-0.5 text-brand-600 dark:text-brand-400" weight="bold" />;
    default:
      return <Check size={12} className="ml-0.5 text-muted-foreground" />;
  }
}

function DocIcon({ icon, size = 14 }: { icon: 'pdf' | 'doc' | 'txt'; size?: number }) {
  switch (icon) {
    case 'pdf':
      return <FilePdf size={size} weight="duotone" className="text-red-500" />;
    case 'doc':
      return <FileDoc size={size} weight="duotone" className="text-blue-500" />;
    default:
      return <FileText size={size} weight="duotone" className="text-muted-foreground" />;
  }
}

function AttachmentIcon({ mimeType, size = 14, isMe }: { mimeType: string; size?: number; isMe: boolean }) {
  const baseClass = isMe ? 'text-white/80' : '';
  if (mimeType.includes('pdf')) {
    return <FilePdf size={size} weight="duotone" className={isMe ? 'text-white/80' : 'text-red-500'} />;
  }
  if (mimeType.includes('word') || mimeType.includes('doc')) {
    return <FileDoc size={size} weight="duotone" className={isMe ? 'text-white/80' : 'text-blue-500'} />;
  }
  return <File size={size} weight="duotone" className={baseClass || 'text-muted-foreground'} />;
}

// ─── helpers ────────────────────────────────────────────────────

export function threadDisplayName(t: MessageThread, currentUserId?: string): string {
  if (t.subject) return t.subject;
  if (t.case) return `${t.case.caseNumber} ${t.case.caseName}`;
  const others = t.participants
    .filter((p) => p.userId !== currentUserId)
    .map((p) => `${p.user.firstName} ${p.user.lastName}`);
  const first = others[0];
  if (!first) return 'New conversation';
  if (others.length === 1) return first;
  return `${first} + ${others.length - 1} others`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d.toISOString(), today.toISOString())) return 'Today';
  if (sameDay(d.toISOString(), yesterday.toISOString())) return 'Yesterday';
  return d.toLocaleDateString([], {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

function sameDay(aIso: string, bIso: string): boolean {
  const a = new Date(aIso);
  const b = new Date(bIso);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
